import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { accessControlService, AppAccess } from '../services/accessControl';
import { extractOrgIdFromPath } from '../utils/organizationUtils';

export interface UseAppAccessReturn {
  accessibleApps: AppAccess[];
  loading: boolean;
  error: string | null;
  hasAccess: (appName: string) => boolean;
  refreshAccess: () => Promise<void>;
}

/**
 * Hook for managing app access control
 * Provides access to user's accessible apps and permission checking
 */
export const useAppAccess = (): UseAppAccessReturn => {
  const [accessibleApps, setAccessibleApps] = useState<AppAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // Get current organization from URL
  const orgId = extractOrgIdFromPath(location.pathname);

  const loadAppAccess = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Set current organization in service
      accessControlService.setCurrentOrganization(orgId);
      
      const result = await accessControlService.getAccessibleApps();
      
      if (result.error) {
        setError(result.error);
        setAccessibleApps([]);
        return;
      }

      setAccessibleApps(result.data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load app access';
      setError(errorMessage);
      setAccessibleApps([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const hasAccess = useCallback((appName: string): boolean => {
    return accessibleApps.some(app => app.app_name === appName);
  }, [accessibleApps]);

  const refreshAccess = useCallback(async () => {
    await loadAppAccess();
  }, [loadAppAccess]);

  useEffect(() => {
    loadAppAccess();
  }, [loadAppAccess, orgId]);

  return {
    accessibleApps,
    loading,
    error,
    hasAccess,
    refreshAccess
  };
};
